"use client"

import { useState } from "react"
import { useApi, API_ENDPOINTS } from "@/lib/api"
import { useToast } from "@/hooks/use-toast"
import { useFormatters } from "@/hooks/use-formatters"
import { saveAs } from "file-saver"

// Constants for engagement document operations
const ENGAGEMENT_ENTITY_TYPE_ID = 160
const ENGAGEMENT_DOCUMENT_TYPE_ID = 160001

interface UseDocumentsProps {
  recordId?: number
  dictionary?: any
}

export interface DocumentApiResponse {
  Items: DocumentItem[]
  NextPageLink: string | null
  Count: number | null
}

export interface DocumentItem {
  Id: number
  PublicId: string
  PdfDocumentPublicId: string
  Name: string
  Extension: string
  Size: number
  Description: string
  EntityTypeId: number
  EntityId: number
  DocumentTypeId: number
  CreatedByProfileId: number
  CreatedDatetime: string
  UploadedByProfileId: number
  UploadedDatetime: string
  LastModifiedByProfileId: number
  LastModifiedDatetime: string
  UploadedByContactFirstName: string
  UploadedByContactLastName: string
  UploadedByFullName: string
  ESignedStatusId: number
}

interface UseDocumentsReturn {
  isUploading: boolean
  isFetching: boolean
  uploadDocument: (file: File, documentName: string) => Promise<boolean>
  removeDocument: (publicId: string) => Promise<boolean>
  viewDocument: (publicId: string) => Promise<void>
  downloadDocument: (publicId: string) => Promise<void>
  fetchDocuments: () => Promise<DocumentItem[]>
  formatFileSize: (bytes: number) => string
}

// Helper function to create FormData for file upload
function createUploadFormData(
  file: File,
  entityTypeId: number,
  entityId: number,
  documentTypeId: number,
  uploadCommandId: string,
  documentName: string
): FormData {
  const formData = new FormData()
  
  // Add the file
  formData.append('file', file)
  
  // Add all the required form fields from the API spec
  formData.append('entityTypeId', entityTypeId.toString())
  formData.append('entityId', entityId.toString())
  formData.append('documentTypeId', documentTypeId.toString()) 
  formData.append('description', documentName)
  formData.append('isFinalDocument', 'true')
  formData.append('documentSubEntityId', 'undefined')
  formData.append('existingDocumentName', documentName) // Use the custom document name
  formData.append('documentTypeName', 'undefined')
  formData.append('multiDocumentUploadBatchId', uploadCommandId)
  
  return formData
}

export function useDocuments({ recordId, dictionary }: UseDocumentsProps): UseDocumentsReturn {
  const { fetchWithAuth, mainApi } = useApi()
  const { toast } = useToast()
  const [isUploading, setIsUploading] = useState(false)
  const [isFetching, setIsFetching] = useState(false)

  const { formatDecimal } = useFormatters()

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes"
    const k = 1024
    const sizes = ["Bytes", "KB", "MB", "GB"]
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    const value = bytes / Math.pow(k, i)
    return formatDecimal(value) + " " + sizes[i]
  }

  const uploadDocument = async (file: File, documentName: string): Promise<boolean> => {
    if (!file || !documentName) return false
    
    // Generate a command ID
    const uploadCommandId = crypto.randomUUID()
    
    // Use the correct values for engagement documents
    const entityTypeId = ENGAGEMENT_ENTITY_TYPE_ID
    const entityId = recordId
    const documentTypeId = ENGAGEMENT_DOCUMENT_TYPE_ID
    
    if (!entityId) {
      toast({
        title: dictionary?.engagement?.documents?.uploadError || "Upload Failed",
        description: "No engagement record ID provided",
        variant: "destructive",
      })
      return false
    }
    
    setIsUploading(true)
    
    try {
      // Create FormData using helper function
      const formData = createUploadFormData(file, entityTypeId, entityId, documentTypeId, uploadCommandId, documentName)

      // Make the API call using fetchWithAuth directly instead of mainApi.post()
      // because mainApi.post() automatically sets Content-Type: application/json and calls JSON.stringify()
      // For file uploads with FormData, we need the browser to set Content-Type with proper multipart boundary
      
      // Use the centralized API endpoint configuration
      const uploadUrl = `${API_ENDPOINTS.MAIN.BASE_URL}/api/command/postfile?commandId=${uploadCommandId}`
      
      await fetchWithAuth(uploadUrl, {
        method: 'POST',
        body: formData,
        // Don't set Content-Type header - let the browser set it with boundary for multipart/form-data
      })

      // Show success toast
      toast({
        title: dictionary?.engagement?.documents?.uploadSuccess || "Upload Successful",
        description: dictionary?.engagement?.documents?.uploadSuccessDescription || `${documentName} has been uploaded successfully.`,
        duration: 3000, // Auto-dismiss after 3 seconds
      })
      return true
    } catch (error) {
      console.error('Document upload failed:', error)
      // Show error toast
      toast({
        title: dictionary?.engagement?.documents?.uploadError || "Upload Failed",
        description: `${dictionary?.engagement?.documents?.uploadErrorDescription || "Failed to upload document"}: ${error instanceof Error ? error.message : 'Unknown error'}`,
        variant: "destructive",
      })
      return false
    } finally {
      setIsUploading(false)
    }
  }

  const removeDocument = async (publicId: string): Promise<boolean> => {
    if (!publicId) return false

    try {
      const requestBody = {
        CommandName: "RemoveDocument",
        PublicId: publicId,
        IncludeChildren: true
      }

      await mainApi.post('/api/command/RemoveDocument/synchronous', requestBody)

      // Show success toast
      toast({
        title: dictionary?.engagement?.documents?.deleteSuccess || "Document Deleted",
        description: dictionary?.engagement?.documents?.deleteSuccessDescription || "Document has been deleted successfully.",
        duration: 3000, // Auto-dismiss after 3 seconds
      })
      return true
    } catch (error) {
      console.error('Document deletion failed:', error)
      // Show error toast
      toast({
        title: dictionary?.engagement?.documents?.deleteError || "Delete Failed",
        description: `${dictionary?.engagement?.documents?.deleteErrorDescription || "Failed to delete document"}: ${error instanceof Error ? error.message : 'Unknown error'}`,
        variant: "destructive",
      })
      return false
    }
  }

  // Helper function to get document access token and URL
  const getDocumentAccessUrl = async (publicId: string, isPdf: boolean): Promise<string> => {
    const tokenResponse = await mainApi.get<string>(`/api/report/getDownloadAccess/${publicId}?pdf=${isPdf}`)
    const accessToken = tokenResponse
    return `${API_ENDPOINTS.MAIN.BASE_URL}/api/report/download?tx=${accessToken}`
  }

  const viewDocument = async (publicId: string): Promise<void> => {
    if (!publicId) return

    try {
      // View as PDF for consistent browser display
      const downloadUrl = await getDocumentAccessUrl(publicId, true)
      window.open(downloadUrl, '_blank')
    } catch (error) {
      console.error('Document view failed:', error)
      toast({
        title: dictionary?.engagement?.documents?.viewError || "View Failed",
        description: `${dictionary?.engagement?.documents?.viewErrorDescription || "Failed to view document"}: ${error instanceof Error ? error.message : 'Unknown error'}`,
        variant: "destructive",
      })
    }
  }

  const downloadDocument = async (publicId: string): Promise<void> => {
    if (!publicId) return

    try {
      // Download original format for editing
      const downloadUrl = await getDocumentAccessUrl(publicId, false)
      
      // Use fetch to download file as blob
      const response = await fetch(downloadUrl)
      if (!response.ok) throw new Error(`HTTP ${response.status}`)
      
      const blob = await response.blob()
      
      // Get filename from response headers or use a default
      const contentDisposition = response.headers.get('content-disposition')
      const filename = contentDisposition 
        ? contentDisposition.split('filename=')[1]?.replace(/['"]/g, '') 
        : 'document'
      
      // Use file-saver to handle the download
      saveAs(blob, filename)
    } catch (error) {
      console.error('Document download failed:', error)
      toast({
        title: dictionary?.engagement?.documents?.downloadError || "Download Failed",
        description: `${dictionary?.engagement?.documents?.downloadErrorDescription || "Failed to download document"}: ${error instanceof Error ? error.message : 'Unknown error'}`,
        variant: "destructive",
      })
    }
  }

  const fetchDocuments = async (): Promise<DocumentItem[]> => {
    if (!recordId) {
      console.warn('No recordId provided for fetchDocuments')
      return []
    }

    const entityTypeId = ENGAGEMENT_ENTITY_TYPE_ID
    const entityId = recordId

    setIsFetching(true)
    
    try {
      // Make the API call to get documents for the entity using mainApi
      const response = await mainApi.get<DocumentApiResponse>(`/api/document/${entityTypeId}/${entityId}`)

      return response.Items || []
    } catch (error) {
      console.error('Failed to fetch documents:', error)
      toast({
        title: dictionary?.engagement?.documents?.fetchError || "Failed to Load Documents",
        description: `${dictionary?.engagement?.documents?.fetchErrorDescription || "Could not load existing documents"}: ${error instanceof Error ? error.message : 'Unknown error'}`,
        variant: "destructive",
      })
      return []
    } finally {
      setIsFetching(false)
    }
  }

  return {
    isUploading,
    isFetching,
    uploadDocument,
    removeDocument,
    viewDocument,
    downloadDocument,
    fetchDocuments,
    formatFileSize,
  }
} 